import React from 'react';
import { Thermometer, Droplets, Wind, Sun } from 'lucide-react';

const EnvironmentalMonitoring: React.FC = () => {
  // Mock data - in real app this would come from environmental sensors
  const environmentData = {
    temperature: {
      average: 72.4,
      range: { min: 70.1, max: 74.8 },
      zones: [
        { name: 'North Wing', temp: 71.2 },
        { name: 'South Wing', temp: 73.1 },
        { name: 'East Wing', temp: 72.8 },
        { name: 'West Wing', temp: 72.5 }
      ]
    },
    humidity: {
      average: 45,
      optimal: true,
      zones: [
        { name: 'North Wing', level: 44 },
        { name: 'South Wing', level: 46 },
        { name: 'East Wing', level: 45 },
        { name: 'West Wing', level: 45 }
      ]
    },
    airQuality: {
      score: 92,
      co2: 612,
      tvoc: 420,
      zones: [
        { name: 'North Wing', score: 94 },
        { name: 'South Wing', score: 91 },
        { name: 'East Wing', score: 93 },
        { name: 'West Wing', score: 90 }
      ]
    },
    lighting: {
      averageLux: 520,
      naturalLight: '68%',
      zones: [
        { name: 'North Wing', level: 540 },
        { name: 'South Wing', level: 510 },
        { name: 'East Wing', level: 530 },
        { name: 'West Wing', level: 500 }
      ]
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Environmental Monitoring</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Thermometer className="text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">Temperature</h3>
          </div>
          <p className="text-3xl font-bold">{environmentData.temperature.average}°F</p>
          <p className="text-gray-500">Average</p>
          <p className="text-sm text-gray-400 mt-1">
            Range: {environmentData.temperature.range.min}° - {environmentData.temperature.range.max}°
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Droplets className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Humidity</h3>
          </div>
          <p className="text-3xl font-bold">{environmentData.humidity.average}%</p>
          <p className="text-gray-500">
            {environmentData.humidity.optimal ? 'Optimal Level' : 'Needs Adjustment'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Wind className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Air Quality</h3>
          </div>
          <p className="text-3xl font-bold">{environmentData.airQuality.score}/100</p>
          <p className="text-gray-500">CO₂: {environmentData.airQuality.co2} ppm</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Sun className="text-yellow-500 mr-2" />
            <h3 className="text-lg font-semibold">Lighting</h3>
          </div>
          <p className="text-3xl font-bold">{environmentData.lighting.averageLux}</p>
          <p className="text-gray-500">Average Lux</p>
          <p className="text-sm text-gray-400 mt-1">
            Natural Light: {environmentData.lighting.naturalLight}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Temperature by Zone</h3>
          <div className="space-y-4">
            {environmentData.temperature.zones.map((zone, index) => (
              <div key={index} className="flex items-center justify-between">
                <p className="font-medium">{zone.name}</p>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className="bg-red-500 rounded-full h-2"
                      style={{ width: `${((zone.temp - 65) / 15) * 100}%` }}
                    ></div>
                  </div>
                  <span>{zone.temp}°F</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Air Quality by Zone</h3>
          <div className="space-y-4">
            {environmentData.airQuality.zones.map((zone, index) => (
              <div key={index} className="flex items-center justify-between">
                <p className="font-medium">{zone.name}</p>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className="bg-green-500 rounded-full h-2"
                      style={{ width: `${zone.score}%` }}
                    ></div>
                  </div>
                  <span>{zone.score}/100</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalMonitoring; 